import type * as CapacitorHaptics from "@capacitor/haptics";
import { isNativeApp, platform } from "./platform.ts";

let enabled = true;
let native: Promise<typeof CapacitorHaptics> | null = null;
let iosSwitch: HTMLLabelElement | null = null;

export function setVibrationEnabled(value: boolean): void {
  enabled = value;
}

export function vibrate(pattern: number | number[]): void {
  if (!enabled) return;
  if (isNativeApp()) {
    // Android WebView ignores navigator.vibrate; the Capacitor plugin does not.
    native ??= import("@capacitor/haptics");
    const pulses = Array.isArray(pattern) ? pattern.filter((_, i) => i % 2 === 0) : [pattern];
    void native
      .then(({ Haptics, ImpactStyle }) =>
        pulses.length > 1 || (pulses[0] ?? 0) > 20 ? Haptics.impact({ style: ImpactStyle.Medium }) : Haptics.impact({ style: ImpactStyle.Light }),
      )
      .catch(() => undefined);
    return;
  }
  if (platform.ios()) {
    iosTicks(pattern);
    return;
  }
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // Some WebViews throw when vibration is not permitted.
  }
}

/**
 * Safari has no navigator.vibrate, but since iOS 18 flipping an
 * `<input type="checkbox" switch>` plays the system haptic tick. One tick per
 * pulse of the pattern, spaced by the pattern's timing so a celebration still
 * reads as "tick tick tick". The first tick fires synchronously so it stays
 * inside the player's tap.
 */
function iosTicks(pattern: number | number[]): void {
  if (typeof document === "undefined") return;
  const steps = Array.isArray(pattern) ? pattern : [pattern];
  let at = 0;
  for (let i = 0; i < steps.length; i += 2) {
    if (at === 0) iosTick();
    else setTimeout(iosTick, at);
    // iOS swallows ticks closer together than this.
    at += Math.max(70, (steps[i] ?? 0) + (steps[i + 1] ?? 0));
  }
}

function iosTick(): void {
  if (!iosSwitch) {
    const label = document.createElement("label");
    label.setAttribute("aria-hidden", "true");
    // Off screen rather than display:none, which stops the switch from ticking.
    label.style.cssText = "position:fixed;left:-100px;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.setAttribute("switch", "");
    input.tabIndex = -1;
    label.append(input);
    // Keep these synthetic clicks away from any listener higher up.
    label.addEventListener("click", (event) => event.stopPropagation());
    document.body.append(label);
    iosSwitch = label;
  }
  iosSwitch.click();
}
